'use client';
import { useEffect, useState } from 'react';
import { useT } from '@/lib/i18n';
import { COUNTRIES, INSTRUMENTS } from '@/lib/constants';

type Entry = {
  userId: string;
  userName: string | null;
  userImage: string | null;
  score: number;
  accuracy?: number;
  createdAt?: string;
};

type Tab = 'song' | 'global' | 'country' | 'instrument';

function Avatar({ image, name }: { image: string | null; name: string | null }) {
  if (image) {
    return (
      /* eslint-disable-next-line @next/next/no-img-element */
      <img src={image} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
    );
  }
  return (
    <div className="w-8 h-8 rounded-full bg-violet-600 flex items-center justify-center text-sm font-bold text-white shrink-0">
      {(name ?? '?')[0].toUpperCase()}
    </div>
  );
}

export default function LeaderboardPage() {
  const t = useT();
  const [tab, setTab] = useState<Tab>('song');
  const [songs, setSongs] = useState<string[]>([]);
  const [selectedSong, setSelectedSong] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedInstrument, setSelectedInstrument] = useState('');
  const [entries, setEntries] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetch('/api/leaderboard/songs')
      .then(r => (r.ok ? r.json() : []))
      .then((list: string[]) => {
        setSongs(list);
        if (list.length > 0) setSelectedSong(prev => prev || list[0]);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    let url = '';
    if (tab === 'global') {
      url = '/api/leaderboard?global=true';
    } else if (tab === 'song' && selectedSong) {
      url = `/api/leaderboard?song=${encodeURIComponent(selectedSong)}`;
    } else if (tab === 'country' && selectedCountry) {
      url = `/api/leaderboard?country=${encodeURIComponent(selectedCountry)}`;
    } else if (tab === 'instrument' && selectedInstrument) {
      url = `/api/leaderboard?instrument=${encodeURIComponent(selectedInstrument)}`;
    }
    if (!url) { setEntries([]); return; }
    setLoading(true);
    fetch(url)
      .then(r => (r.ok ? r.json() : []))
      .then(setEntries)
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [tab, selectedSong, selectedCountry, selectedInstrument]);

  const formatDate = (iso?: string) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' });
  };

  const TABS: { id: Tab; label: string }[] = [
    { id: 'song', label: t('leaderboard.bySong') },
    { id: 'global', label: t('leaderboard.global') },
    { id: 'country', label: t('leaderboard.byCountry') },
    { id: 'instrument', label: t('leaderboard.byInstrument') },
  ];

  return (
    <div className="max-w-3xl mx-auto px-6 py-10 space-y-6">
      <h1 className="text-2xl font-bold tracking-tight">{t('leaderboard.title')}</h1>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2">
        {TABS.map(({ id, label }) => (
          <button key={id} onClick={() => setTab(id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === id ? 'bg-violet-600 text-white' : 'bg-sky-200 text-slate-600 hover:bg-sky-300'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Filter row */}
      {tab === 'song' && (
        <select value={selectedSong} onChange={e => setSelectedSong(e.target.value)}
          className="w-full bg-sky-200 border border-sky-300 rounded-lg px-3 py-2 text-slate-900">
          {songs.length === 0 && <option value="">—</option>}
          {songs.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      )}
      {tab === 'country' && (
        <select value={selectedCountry} onChange={e => setSelectedCountry(e.target.value)}
          className="w-full bg-sky-200 border border-sky-300 rounded-lg px-3 py-2 text-slate-900">
          <option value="">{t('leaderboard.selectCountry')}</option>
          {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
        </select>
      )}
      {tab === 'instrument' && (
        <select value={selectedInstrument} onChange={e => setSelectedInstrument(e.target.value)}
          className="w-full bg-sky-200 border border-sky-300 rounded-lg px-3 py-2 text-slate-900">
          <option value="">{t('leaderboard.selectInstrument')}</option>
          {INSTRUMENTS.map(i => <option key={i} value={i}>{t(`instrument.${i}`)}</option>)}
        </select>
      )}

      {/* Table */}
      <div className="bg-sky-100 rounded-2xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="text-xs text-slate-500 uppercase tracking-wider border-b border-sky-200">
              <th className="text-left px-4 py-3 w-10">{t('leaderboard.rank')}</th>
              <th className="text-left px-4 py-3">{t('leaderboard.player')}</th>
              <th className="text-right px-4 py-3">{t('leaderboard.score')}</th>
              {tab === 'song' && (
                <>
                  <th className="text-right px-4 py-3 hidden sm:table-cell">{t('leaderboard.accuracy')}</th>
                  <th className="text-right px-4 py-3 hidden md:table-cell">{t('leaderboard.date')}</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="py-12 text-center text-slate-400 text-sm">
                  <span className="inline-block animate-pulse">…</span>
                </td>
              </tr>
            ) : entries.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-12 text-center text-slate-400 text-sm">—</td>
              </tr>
            ) : (
              entries.map((entry, i) => (
                <tr key={entry.userId} className="border-t border-sky-200/60 hover:bg-sky-200/30 transition-colors">
                  <td className="px-4 py-3 font-mono text-sm text-slate-500 w-10">
                    {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : i + 1}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Avatar image={entry.userImage} name={entry.userName} />
                      <span className="font-medium truncate max-w-[140px]">{entry.userName ?? '—'}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-violet-600">
                    {entry.score.toLocaleString()}
                  </td>
                  {tab === 'song' && (
                    <>
                      <td className="px-4 py-3 text-right text-slate-600 hidden sm:table-cell">
                        {entry.accuracy != null ? `${Math.round(entry.accuracy * 100)}%` : '—'}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-400 text-sm hidden md:table-cell">
                        {formatDate(entry.createdAt)}
                      </td>
                    </>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
